<template>
    <page :busy="busy" :tabs="tabs">
        <div class="lx-printable">
            <div class="lx-print-only">
                <h4>Fees and Services Report</h4>
                {{ formattedDates }}
            </div>
            <grid
                ref="grid"
                :defaultColDef="defaultColDef"
                :autoGroupColumnDef="autoGroupColumnDef"
                :columnDefs="columnDefs"
                :treeData="true"
                :getDataPath="getDataPath"
                :rowData="gridRowData"
                :isGroupOpenByDefault="isGroupOpenByDefault"
            >
                <date-range-picker
                    :range-options="dateRangePickerOptions"
                    :value="datesValue"
                    :date-format="userUnicodeDateFormat"
                    @input="onDateRangePickerInput"
                ></date-range-picker>
                <responsive-dropdown :icon="['fas', 'layer-group']" :items="groupingTypeOptions" variant="primary">{{
                    groupingTypeLabel
                }}</responsive-dropdown>
                <responsive-button
                    :icon="['fas', 'up-right-and-down-left-from-center']"
                    variant="primary"
                    @click="onButtonExpand"
                    >Expand</responsive-button
                >
                <responsive-button :icon="['fas', 'print']" variant="primary" @click="onButtonPrint"
                    >Print</responsive-button
                >
                <export-to-excel-button :href="excelHref" />
                <report-settings-modal
                    :modalId="reportSettingsModalId"
                    :initialValues="reportSettings"
                    :excelOnly="true"
                />
                <fa-icon-tooltip id="icon-info" :icon="['fas', 'circle-info']" class="text-primary h-100" variant="info">
                    <p style="text-align: left">
                        Invoices with departure dates outside of reporting period are not included on this report.
                    </p>
                    <p style="text-align: left">
                        *One-Time Fees are comprised of all one-time fees manually added to invoices.
                    </p>
                </fa-icon-tooltip>
            </grid>
            <p class="lx-print-only lx-notes">
                Invoices with departure dates outside of reporting period are not included on this report.<br />
                *One-Time Fees are comprised of all one-time fees manually added to invoices.
            </p>
        </div>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../api';
    import { escapeHTML } from '../../utils/strings';
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        THIS_YEAR,
        LAST_YEAR,
        ALL_DATES,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import FaIconTooltip from '../generic/fa-icon-tooltip/fa-icon-tooltip.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import ReportSettingsModal, { DATE_PERIODS_INPUT, SELECT_INPUT } from './report-settings/report-settings-modal.vue';

    const SERVER_DATE_FORMAT = 'yyyy-MM-dd';
    const GROUPING_TYPE_NAME = 'name';
    const GROUPING_TYPE_GROUP = 'group';
    const GROUPING_TYPES = [GROUPING_TYPE_NAME, GROUPING_TYPE_GROUP];
    const GROUPING_TYPE_NAME_LABEL = 'Group fees by name';
    const GROUPING_TYPE_GROUP_LABEL = 'Group fees by group';
    const GROUPING_TYPE_LABELS = [GROUPING_TYPE_NAME_LABEL, GROUPING_TYPE_GROUP_LABEL];

    export default {
        name: 'ReportFeesServices',
        components: {
            Page,
            Grid,
            DateRangePicker,
            ResponsiveButton,
            ResponsiveDropdown,
            FaIconTooltip,
            ExportToExcelButton,
            ReportSettingsModal,
        },
        mixins: [removeScrollMixin],
        data() {
            const defaultDates = this.$store.getters['reportFeeServicesLocalStorage/dateRange'];
            const defaultGroupingType = this.$store.getters['reportFeeServicesLocalStorage/groupingType'];
            return {
                busy: false,
                groupingType: GROUPING_TYPES[defaultGroupingType],
                groupingTypeLabel: GROUPING_TYPE_LABELS[defaultGroupingType],
                // Grid
                gridRowData: [],
                defaultColDef: {
                    minWidth: 80,
                    sortable: false,
                },
                autoGroupColumnDef: {
                    flex: 2,
                    minWidth: 300,
                    resizable: true,
                    sortable: true,
                    sort: 'asc',
                    comparator: (valueA, valueB, nodeA, nodeB) => {
                        const nameA = nodeA.data.property_name || nodeA.data.name;
                        const nameB = nodeB.data.property_name || nodeB.data.name;
                        return nameA > nameB ? 1 : -1;
                    },
                    headerName: 'Fee / Service / Property',
                    cellStyle: { textAlign: 'center' },
                    cellRendererParams: {
                        innerRenderer: ({ data }) => escapeHTML(data.property_name || data.name),
                    },
                },
                getDataPath: (data) => data.id_hierarchy,
                gridContainerEl: null,
                gridApi: null,
                gridColumnApi: null,
                expanded: false,
                // Date-range Picker
                dates: null,
                defaultDates,
                dateRangePickerOptions: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR, ALL_DATES],
                reportSettingsModalId: 'report-settings-modal',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            tabs() {
                return [
                    {
                        title: `Fees & Services Report ${this.formattedDates}`,
                        route: '/app/reports/fees-services/',
                        active: true,
                    },
                ];
            },
            columnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Fee / Service',
                        hide: true,
                    },
                    {
                        field: 'property_name',
                        headerName: 'Property',
                        hide: true,
                    },
                    {
                        field: 'net',
                        headerName: 'Net',
                        type: 'amountColumn',
                    },
                    {
                        field: 'tax',
                        headerName: 'Tax',
                        valueGetter: ({ data }) => data.gross - data.net,
                        type: 'amountColumn',
                    },
                    {
                        field: 'gross',
                        headerName: 'Gross',
                        type: 'amountColumn',
                    },
                ];
            },
            datesValue: {
                get() {
                    if (!this.dates) {
                        this.dates = nameToRange(this.defaultDates.name);
                        if (!this.dates.from) {
                            this.dates = { ...this.defaultDates };
                        }
                    }
                    return this.dates;
                },
                set(val) {
                    this.dates = val;
                },
            },
            formattedDates() {
                if (!this.datesValue.from || !this.datesValue.to) {
                    return '';
                }
                const formattedFrom = format(this.datesValue.from, this.userUnicodeDateFormat);
                const formattedTo = format(this.datesValue.to, this.userUnicodeDateFormat);
                return `${formattedFrom} - ${formattedTo}`;
            },
            groupingTypeOptions() {
                return [
                    {
                        label: GROUPING_TYPE_NAME_LABEL,
                        click: () => this.onGroupFeesByName(),
                    },
                    {
                        label: GROUPING_TYPE_GROUP_LABEL,
                        click: () => this.onGroupFeesByGroup(),
                    },
                ];
            },
            excelHref() {
                return `/app/reports/fees-services/row-data/excel/?from_date=${format(
                    this.datesValue.from,
                    SERVER_DATE_FORMAT
                )}&to_date=${format(this.datesValue.to, SERVER_DATE_FORMAT)}&group=${this.groupingType}`;
            },
            reportSettings() {
                return {
                    name: 'Fees Services',
                    report_sysname: 'FEES_SERVICES',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            predefinedRanges: this.dateRangePickerOptions,
                            label: 'Period',
                            name: 'from_date',
                        },
                        {
                            type: SELECT_INPUT,
                            value: GROUPING_TYPE_GROUP,
                            name: 'group',
                            options: [
                                {
                                    text: GROUPING_TYPE_NAME_LABEL,
                                    value: GROUPING_TYPE_NAME,
                                },
                                {
                                    text: GROUPING_TYPE_GROUP_LABEL,
                                    value: GROUPING_TYPE_GROUP,
                                },
                            ],
                            label: 'Group By',
                        },
                    ],
                };
            },
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridContainerEl = grid.$refs.gridContainer;
            this.gridApi = grid.gridOptions.api;
            this.gridColumnApi = grid.gridOptions.api;
            this.fetchGridRowData();
            this.gridApi.forEachLeafNode((item) => {
                console.log(item);
            });
        },
        methods: {
            async fetchGridRowData() {
                this.busy = true;
                try {
                    const response = await API.get('/app/reports/fees-services/row-data/', {
                        params: {
                            from_date: format(this.datesValue.from, SERVER_DATE_FORMAT),
                            to_date: format(this.datesValue.to, SERVER_DATE_FORMAT),
                            group: this.groupingType,
                        },
                    });
                    this.gridRowData = response.data;
                } catch (err) {
                    this.errorToast({ text: 'Could not load report data.' });
                    console.log(err);
                } finally {
                    this.busy = false;
                }
            },
            expandLevel0() {
                this.gridApi.collapseAll();
                this.gridApi.forEachNode((node) => {
                    if (node.level === 1) {
                        node.setExpanded(true);
                    }
                });
            },
            isGroupOpenByDefault(params) {
                return params.rowNode.level === 0;
            },
            async onDateRangePickerInput(dates) {
                this.datesValue = { ...dates };
                this.$store.dispatch('reportFeeServicesLocalStorage/changeDateRange', dates);
                await this.fetchGridRowData();
            },
            async onGroupFeesByName() {
                if (this.groupingType === GROUPING_TYPE_NAME) {
                    return;
                }
                this.groupingType = GROUPING_TYPE_NAME;
                this.groupingTypeLabel = GROUPING_TYPE_NAME_LABEL;
                this.$store.dispatch(
                    'reportFeeServicesLocalStorage/changeGroupingType',
                    GROUPING_TYPES.indexOf(GROUPING_TYPE_NAME)
                );
                await this.fetchGridRowData();
            },
            async onGroupFeesByGroup() {
                if (this.groupingType === GROUPING_TYPE_GROUP) {
                    return;
                }
                this.groupingType = GROUPING_TYPE_GROUP;
                this.groupingTypeLabel = GROUPING_TYPE_GROUP_LABEL;
                this.$store.dispatch(
                    'reportFeeServicesLocalStorage/changeGroupingType',
                    GROUPING_TYPES.indexOf(GROUPING_TYPE_GROUP)
                );
                await this.fetchGridRowData();
            },
            async onButtonRefresh() {
                await this.fetchGridRowData();
            },
            onButtonExpand() {
                if (this.expanded) {
                    this.expandLevel0();
                } else {
                    this.gridApi.expandAll();
                }
                this.expanded = !this.expanded;
            },
            onButtonPrint() {
                // Set printer friendly
                this.busy = true;
                document.body.classList.add('lx-body-printing');
                this.gridContainerEl.style.width = '200mm';
                const elHeight = this.gridContainerEl.style.height;
                this.gridContainerEl.style.height = 'auto';
                setTimeout(() => {
                    this.gridApi.setGridOption('domLayout', 'print');
                    this.gridApi.forEachNode((row) => row.setRowHeight(21));
                    this.gridColumnApi.autoSizeAllColumns();
                    setTimeout(() => {
                        this.busy = false;
                        print();
                        // Set normal
                        document.body.classList.remove('lx-body-printing');
                        this.gridContainerEl.style.width = 'auto';
                        this.gridContainerEl.style.height = elHeight;
                        setTimeout(() => {
                            this.gridApi.setGridOption('domLayout', 'auto');
                            this.gridApi.resetRowHeights();
                            this.gridApi.sizeColumnsToFit();
                        });
                    }, 2000);
                });
            },
            onButtonExport() {
                const from = format(this.datesValue.from, 'yyyyMMdd');
                const to = format(this.datesValue.to, 'yyyyMMdd');
                this.gridApi.exportDataAsExcel({
                    fileName: `${from}-${to}-fees-services.xlsx`,
                    sheetName: 'Fees and Services',
                    columnWidth: ({ index }) => (index < 2 ? 200 : 100),
                    columnKeys: ['name', 'property_name', 'net', 'tax', 'gross'],
                    shouldRowBeSkipped: (params) => !params.node.data.property_name,
                    processRowGroupCallback: () => '',
                });
            },
        },
    };
</script>

<style lang="scss" scoped>
    .lx-printable {
        height: 100%;
    }
    .lx-print-only {
        display: none;
    }
    @media print {
        .lx-printable {
            visibility: visible;
            position: absolute;
            left: 0;
            top: 0;
            height: 0;
            overflow: visible;
        }
        .lx-print-only {
            display: block;
        }
    }
    .lx-notes {
        margin-top: 4px;
        font-size: 12px;
    }
</style>
